import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadActivities, loadIndex, loadItineraries, loadMentions, loadPlaceIndex, loadPlaces } from '../data/load';
import type { Activity, ChapterIndex, Itinerary, Mention, PlaceFeature, PlaceIndexEntry } from '../data/types';
import { byReadingOrder } from '../features/reader/segments';
import { citiesOf, resolveCity, type CityScope } from './scope';

/** Lo que se descarga una vez: el índice de capítulos, los ámbitos y el índice global de lugares.
 *  Los datos pesados van por colección (fase E), en `CollectionData`. */
interface RawData {
  index: ChapterIndex;
  cities: CityScope[];
  placeIndex: PlaceIndexEntry[];
  /** capítulo → colección, para saber a qué ámbito pertenece un pasaje cualquiera del libro */
  chapterCollection: Map<string, string>;
}

interface CollectionData {
  places: PlaceFeature[];
  mentions: Mention[];
  itineraries: Itinerary[];
  activities: Activity[];
}

interface AppData extends RawData {
  places: PlaceFeature[];
  placeById: Map<string, PlaceFeature>;
  /** menciones de lugares publicados, en orden de lectura */
  mentions: Mention[];
  mentionsByPlace: Map<string, Mention[]>;
  mentionsByParagraph: Map<string, Mention[]>;
  mentionById: Map<string, Mention>;
  itineraries: Itinerary[];
  activities: Activity[];
  /** ámbito activo y ámbitos publicados (el libro son seis colecciones) */
  city: CityScope;
}

const RawCtx = createContext<RawData | null>(null);
const Ctx = createContext<AppData | null>(null);

/** Descarga la colección pedida y la deja en caché (la caché vive en data/load.ts). */
export function loadCollection(col: string): Promise<CollectionData> {
  return Promise.all([
    loadPlaces(col),
    loadMentions(col),
    loadItineraries(col).catch(() => [] as Itinerary[]),
    loadActivities(col).catch(() => [] as Activity[]),
  ]).then(([places, allMentions, itineraries, activities]) => {
    const published = new Set(places.map((p) => p.id));
    const mentions = allMentions.filter((m) => published.has(m.placeId) && m.textConfidence !== 'rejected').sort(byReadingOrder);
    return { places, mentions, itineraries, activities };
  });
}

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ data?: RawData; error?: string }>({});
  useEffect(() => {
    Promise.all([loadIndex(), loadPlaceIndex()])
      .then(([index, placeIndex]) => setState({ data: {
        index, placeIndex, cities: citiesOf(index),
        chapterCollection: new Map(index.chapters.map((c) => [c.chapterId, c.collectionId])),
      } }))
      .catch((e: unknown) => setState({ error: e instanceof Error ? e.message : String(e) }));
  }, []);

  if (state.error) return <div className="app-error" role="alert"><h1>No se pudieron cargar los datos</h1><p>{state.error}</p></div>;
  if (!state.data) return <div className="app-loading" role="status">Cargando la edición…</div>;
  return <RawCtx.Provider value={state.data}>{children}</RawCtx.Provider>;
}

/** Acota los datos al ámbito activo y los descarga si aún no están. Va dentro del router porque el
 *  ámbito vive en la URL; cambiar de ámbito descarga el suyo y no toca lo demás. */
export function ScopedDataProvider({ children }: { children: ReactNode }) {
  const raw = useContext(RawCtx);
  if (!raw) throw new Error('ScopedDataProvider fuera de AppDataProvider');
  const [params] = useSearchParams();
  const city = resolveCity(raw.cities, params.get('ciudad'));
  const col = city.collectionId;
  const [state, setState] = useState<{ col?: string; data?: CollectionData; error?: string }>({});
  useEffect(() => {
    let live = true;
    loadCollection(col)
      .then((data) => { if (live) setState({ col, data }); })
      .catch((e: unknown) => { if (live) setState({ col, error: e instanceof Error ? e.message : String(e) }); });
    return () => { live = false; };
  }, [col]);

  const ready = state.col === col ? state : null;
  const data = useMemo<AppData | null>(() => {
    if (!ready?.data) return null;
    const { places, mentions, itineraries, activities } = ready.data;
    const index = { ...raw.index, chapters: raw.index.chapters.filter((c) => c.collectionId === col) };
    const placeById = new Map(places.map((p) => [p.id, p]));
    const group = (key: (m: Mention) => string) => {
      const map = new Map<string, Mention[]>();
      for (const m of mentions) map.set(key(m), [...(map.get(key(m)) ?? []), m]);
      return map;
    };
    return {
      ...raw, index, places, placeById, mentions,
      mentionsByPlace: group((m) => m.placeId),
      mentionsByParagraph: group((m) => m.paragraphId),
      mentionById: new Map(mentions.map((m) => [m.id, m])),
      itineraries, activities, city,
    };
  }, [raw, ready, col, city]);

  if (ready?.error) return <div className="app-error" role="alert"><h1>No se pudieron cargar los datos</h1><p>{ready.error}</p></div>;
  if (!data) return <div className="app-loading" role="status">Cargando {city.inline}…</div>;
  return <Ctx.Provider value={data}>{children}</Ctx.Provider>;
}

/** Los datos comunes a todo el libro, sin acotar a ninguna colección: es lo que necesita la
 *  portada del proyecto, que habla de las seis a la vez y no debe esperar a que baje ninguna. */
export function useBookData(): RawData {
  const v = useContext(RawCtx);
  if (!v) throw new Error('useBookData fuera de AppDataProvider');
  return v;
}

export function useAppData(): AppData {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAppData fuera de AppDataProvider');
  return v;
}

export function useSortedPlaces(): PlaceFeature[] {
  const { places } = useAppData();
  return useMemo(() => [...places].sort((a, b) => a.properties.canonicalName.localeCompare(b.properties.canonicalName, 'es')), [places]);
}
